import versionInfo from "../../version.json";

/**
 * Utilitário para gerenciar a versão da aplicação, detecção de novas versões
 * e invalidação profunda de caches (PWA, Service Workers, Cache Storage).
 */

export const getLocalVersion = () => {
  return versionInfo;
};

/**
 * Busca a versão mais recente diretamente do servidor com no-cache.
 */
export const fetchRemoteVersion = async () => {
  try {
    const timestamp = Date.now();
    const response = await fetch(`/version.json?t=${timestamp}`, {
      method: "GET",
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.warn("[VersionManager] Falha ao consultar versão remota:", error);
    return null;
  }
};

/**
 * Compara a versão local em memória com a versão retornada pelo servidor.
 */
export const hasNewVersionAvailable = (local, remote) => {
  if (!remote || !local) return false;

  // Comparação primária por número semântico de versão (ex: 0.1.11 -> 0.1.12)
  if (remote.version && local.version && remote.version !== local.version) {
    return true;
  }

  // Comparação secundária por commit SHA
  if (
    remote.commit &&
    local.commit &&
    remote.commit !== "dev" &&
    local.commit !== "dev" &&
    remote.commit !== local.commit
  ) {
    return true;
  }

  // Comparação terciária por timestamp de build (se ambos fornecerem)
  if (
    remote.buildTimestamp &&
    local.buildTimestamp &&
    remote.buildTimestamp > local.buildTimestamp
  ) {
    return true;
  }

  return false;
};

/**
 * Limpa todos os caches da aplicação (Cache API do navegador e Service Workers).
 */
export const clearAppCaches = async () => {
  try {
    // 1. Limpa todas as instâncias da Cache Storage API
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
      console.log("[VersionManager] Cache Storage esvaziado com sucesso.");
    }

    // 2. Notifica e atualiza todos os Service Workers registrados
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        if (registration.active) {
          registration.active.postMessage({ type: "CLEAR_CACHE" });
          registration.active.postMessage({ type: "SKIP_WAITING" });
        }
        await registration.update().catch(() => {});
      }
    }
  } catch (error) {
    console.warn("[VersionManager] Erro ao limpar caches:", error);
  }
};

/**
 * Força o recarregamento da aplicação contornando caches do navegador.
 */
export const forceReloadApp = async (targetVersion = "") => {
  const now = Date.now();
  const lastReloadTime = Number(
    sessionStorage.getItem("drive_last_version_reload_time") || 0
  );
  const lastReloadVersion =
    sessionStorage.getItem("drive_last_version_reload_target") || "";

  // Previne loop infinito de recarregamentos se a CDN estiver em transição
  if (
    targetVersion &&
    lastReloadVersion === targetVersion &&
    now - lastReloadTime < 15000
  ) {
    console.warn(
      `[VersionManager] Recarregamento abortado para evitar loop em v${targetVersion}`
    );
    return;
  }

  sessionStorage.setItem("drive_last_version_reload_time", String(now));
  if (targetVersion) {
    sessionStorage.setItem("drive_last_version_reload_target", targetVersion);
  }

  await clearAppCaches();

  // Força reload contornando cache do navegador
  window.location.reload();
};
